import { useEffect, useRef } from 'react';
import { pushAPI } from '../services/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * Requests push notification permission and registers a Web Push subscription
 * for the logged-in user. Silently does nothing on browsers that don't support
 * the Push API (e.g. older Safari).
 *
 * @param {boolean} enabled - pass true when a user is logged in and has 'messages' tab access
 */
export function usePushNotifications(enabled) {
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (subscribedRef.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    let cancelled = false;

    async function subscribe() {
      try {
        // 1. Get VAPID public key
        const { data } = await pushAPI.getVapidKey();
        if (cancelled) return;
        const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

        // 2. Request notification permission (no-op if already granted/denied)
        const permission = await Notification.requestPermission();
        if (permission !== 'granted' || cancelled) return;

        // 3. Get the active service worker registration
        const reg = await navigator.serviceWorker.ready;
        if (cancelled) return;

        // 4. Subscribe (or reuse existing subscription)
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          });
        }
        if (cancelled) return;

        // 5. Send subscription to backend
        const subJson = sub.toJSON();
        await pushAPI.subscribe({
          endpoint: subJson.endpoint,
          keys:     subJson.keys,
        });

        subscribedRef.current = true;
      } catch (err) {
        // Permission denied or unsupported — non-fatal
        console.debug('[push] subscription skipped:', err.message);
      }
    }

    subscribe();
    return () => { cancelled = true; };
  }, [enabled]);
}
